import { useNonogramDailyTopTimes } from '@games/api';
import moment from 'moment';
import React, { FC, Fragment, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import Loader from 'features/puzzle/components/Loader';
import intlMessages from '../../../intl/messages';
import MyLink from './MyLink';
import {
  firstDailyPuzzleYear,
  firstDailyPuzzleMonth,
  firstDailyPuzzleDay,
} from '../../puzzle/constants';

interface DailyTopTimesProps {
  date?: string;
  limit?: number;
  showDate?: boolean;
}

export const DailyTopTimes: FC<DailyTopTimesProps> = ({
  date,
  limit,
  showDate,
}) => {
  const [{ data, loading }] = useNonogramDailyTopTimes(date);
  const dateToUse = date ? moment(date) : moment();
  const nextDate = dateToUse.clone().add(1, 'day');
  const prevDate = dateToUse.clone().subtract(1, 'day');
  const hideBefore = prevDate.isBefore(
    moment()
      .year(firstDailyPuzzleYear)
      .month(firstDailyPuzzleMonth - 1)
      .date(firstDailyPuzzleDay),
    'day',
  );
  const hideAfter = nextDate.isAfter(
    moment().startOf('day'),
    'day',
  );

  const results = useMemo(() => {
    if (!data) return [];
    if (limit) return data.slice(0, limit);
    return data;
  }, [data, limit]);

  return (
    <div className='p-5'>
      <h2 className='text-2xl font-bold mb-3 text-center'>
        <MyLink href={showDate ? '#' : '/daily/top'} useATag>
          <FormattedMessage {...intlMessages.topTimes} />
        </MyLink>
      </h2>
      {showDate && (
        <div className='grid grid-cols-3 items-center mb-3'>
          {hideBefore ? (
            <div />
          ) : (
            <MyLink
              href={
                hideBefore ? '#' : `/daily/top/${prevDate.format('YYYY-MM-DD')}`
              }
              className='px-4 py-2 rounded btn w-fit'
            >
              &lt; <FormattedMessage {...intlMessages.previous} />
            </MyLink>
          )}
          <div className='font-bold text-lg text-center'>
            {dateToUse.format('MMM D, YYYY')}
          </div>
          {hideAfter ? (
            <div />
          ) : (
            <MyLink
              href={
                hideAfter ? '#' : `/daily/top/${nextDate.format('YYYY-MM-DD')}`
              }
              className='px-4 py-2 rounded btn w-fit ml-auto'
            >
              <FormattedMessage {...intlMessages.next} /> &gt;
            </MyLink>
          )}
        </div>
      )}
      {loading ? (
        <Loader />
      ) : (
        <div className='grid grid-cols-3 gap-4'>
          {results.length > 0 ? results.map((time, index) => (
            <Fragment key={time.puzzleId}>
              <div className='font-bold text-left'>#{index + 1}</div>
              <div className='font-bold text-center'>{time.username}</div>
              <div className='font-bold text-right'>
                {time.duration}
              </div>
            </Fragment>
          )) : (
            <div className='col-span-3 text-center'>
              <FormattedMessage {...intlMessages.noTimes} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
